import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageShell from './PageShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { RVP_LIST } from '../data/rendezvousPedagogiques';
import { COURS_VOITURE_ELEVE } from '../data/coursVoitureEleve';
import { loadParticipants } from '../lib/rvpParticipants';
import {
  BookOpen, Presentation, ClipboardCheck, FileText, ArrowRight, Users,
  Plus, ShieldCheck, Sparkles, TrendingUp, Calendar, GraduationCap,
  Rocket, Compass, Trophy, Loader2, Play, MessageCircle,
} from 'lucide-react';

const RVP_ICONS = { Rocket, Compass, Trophy, Presentation };

const RVP_COLOR = {
  sky:      { border: 'border-sky-500/40',      bg: 'bg-sky-950/20',      accent: 'text-sky-300',     button: 'bg-sky-500 hover:bg-sky-400 text-sky-950' },
  amber:    { border: 'border-amber-500/40',    bg: 'bg-amber-950/15',    accent: 'text-amber-300',   button: 'bg-amber-500 hover:bg-amber-400 text-amber-950' },
  emerald:  { border: 'border-emerald-500/40',  bg: 'bg-emerald-950/15',  accent: 'text-emerald-300', button: 'bg-emerald-500 hover:bg-emerald-400 text-emerald-950' },
  red:      { border: 'border-red-500/40',      bg: 'bg-red-950/20',      accent: 'text-red-300',     button: 'bg-red-600 hover:bg-red-500 text-white' },
};

export default function TeacherCoursesHubPage() {
  const { user } = useAuth();
  const [sheets, setSheets] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [sr, er] = await Promise.all([
          api.listSessionSheets().catch(() => ({ sheets: [] })),
          api.listMockExams().catch(() => ({ exams: [] })),
        ]);
        if (!alive) return;
        setSheets(sr.sheets || []);
        setExams(er.exams || []);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Récupérer les participants stockés pour chaque RVP (pour afficher un badge count)
  const rvpsWithParticipants = useMemo(() => {
    return RVP_LIST.map((rvp) => {
      const p = loadParticipants(rvp.slug);
      return { ...rvp, participantsCount: p.eleves?.length || 0 };
    });
  }, []);

  const rvpAac = rvpsWithParticipants.filter((r) => r.formation === 'AAC');
  const rvpAm  = rvpsWithParticipants.filter((r) => r.formation === 'AM');

  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-4 lg:px-6 py-8 sm:py-12" data-testid="teacher-courses-hub">
        {/* Hero */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10">
            <BookOpen size={12} className="text-red-400" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">Mes cours</span>
          </div>
          <h1 className="mt-3 font-serif-editorial text-white text-[36px] sm:text-[48px] leading-tight">
            Tout ton contenu pédagogique
          </h1>
          <p className="mt-2 text-white/60 text-[14px] sm:text-[15px] max-w-3xl">
            Rendez-vous pédagogiques, cours REMC, fiches de séance, examens blancs — tout est ici, à portée de clic.
            {user?.name && <> Bonjour <strong className="text-white">{user.name.split(' ')[0]}</strong>.</>}
          </p>
        </div>

        {/* Actions rapides */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-10">
          <Link to="/enseignant/cours-en-voiture" data-testid="qa-prerequis" className="group rounded-2xl border border-red-500/30 bg-red-950/15 hover:border-red-500/60 hover:bg-red-950/25 p-4 transition">
            <GraduationCap size={20} className="text-red-400 mb-2" />
            <div className="text-white text-[13.5px] font-bold">Top 10 pré-requis</div>
            <div className="mt-1 text-white/50 text-[11.5px]">Visuels cours en voiture</div>
          </Link>
          <Link to="/enseignant/fiche-seance/nouvelle" data-testid="qa-new-sheet" className="group rounded-2xl border border-white/10 bg-[#0d0d0e] hover:border-red-500/50 hover:bg-red-950/10 p-4 transition">
            <FileText size={20} className="text-red-400 mb-2" />
            <div className="text-white text-[13.5px] font-bold">Nouvelle fiche de séance</div>
            <div className="mt-1 text-white/50 text-[11.5px]">QR code élève inclus</div>
          </Link>
          <Link to="/enseignant/examen-blanc-grille" data-testid="qa-new-exam" className="group rounded-2xl border border-white/10 bg-[#0d0d0e] hover:border-red-500/50 hover:bg-red-950/10 p-4 transition">
            <ClipboardCheck size={20} className="text-red-400 mb-2" />
            <div className="text-white text-[13.5px] font-bold">Bilans & Permis blanc</div>
            <div className="mt-1 text-white/50 text-[11.5px]">C1-C4, intersections, AAC…</div>
          </Link>
          <Link to="/enseignant/seances" data-testid="qa-salle-rdv" className="group rounded-2xl border border-white/10 bg-[#0d0d0e] hover:border-red-500/50 hover:bg-red-950/10 p-4 transition">
            <Calendar size={20} className="text-red-400 mb-2" />
            <div className="text-white text-[13.5px] font-bold">Salle & RDV</div>
            <div className="mt-1 text-white/50 text-[11.5px]">Planning des séances</div>
          </Link>
          <Link to="/enseignant/fiches-plateau" data-testid="qa-fiches-plateau" className="group rounded-2xl border border-sky-500/30 bg-sky-950/15 hover:border-sky-500/60 hover:bg-sky-950/25 p-4 transition">
            <div className="text-[20px] mb-1.5">🏍</div>
            <div className="text-white text-[13.5px] font-bold leading-snug">Fiches plateau moto</div>
            <div className="text-white/45 text-[11.5px] mt-0.5">5 séances : déroulé cours / démo / guidage</div>
          </Link>
          <Link to="/cours/progression" data-testid="qa-progression" className="group rounded-2xl border border-white/10 bg-[#0d0d0e] hover:border-red-500/50 hover:bg-red-950/10 p-4 transition">
            <TrendingUp size={20} className="text-red-400 mb-2" />
            <div className="text-white text-[13.5px] font-bold">Ma progression</div>
            <div className="mt-1 text-white/50 text-[11.5px]">Trame 23 étapes REMC</div>
          </Link>
        </div>

        {/* RVP AAC */}
        <div className="mb-10" data-testid="section-rvp-aac">
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-mono-spec font-bold inline-flex items-center gap-1.5">
                <ShieldCheck size={11} /> AAC · Officiel
              </div>
              <h2 className="mt-1 font-serif-editorial text-white text-[24px] sm:text-[28px] leading-tight">Rendez-vous pédagogiques AAC</h2>
            </div>
            <Link to="/enseignant/rendez-vous-pedagogiques" className="text-white/50 hover:text-white text-[12.5px] font-semibold inline-flex items-center gap-1">
              Tout voir <ArrowRight size={12} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rvpAac.map((rvp) => <RvpCard key={rvp.slug} rvp={rvp} />)}
          </div>
        </div>

        {/* AM Formation */}
        <div className="mb-10" data-testid="section-rvp-am">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-sky-400 font-mono-spec font-bold inline-flex items-center gap-1.5">
                <ShieldCheck size={11} /> AM · Officiel
              </div>
              <h2 className="mt-1 font-serif-editorial text-white text-[24px] sm:text-[28px] leading-tight">Formation AM · Séquence 7</h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {rvpAm.map((rvp) => <RvpCard key={rvp.slug} rvp={rvp} />)}
          </div>
        </div>

        {/* Cours REMC voiture */}
        <div className="mb-10" data-testid="section-remc">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-400 font-mono-spec font-bold inline-flex items-center gap-1.5">
                <BookOpen size={11} /> REMC officiel
              </div>
              <h2 className="mt-1 font-serif-editorial text-white text-[24px] sm:text-[28px] leading-tight">Cours voiture · 4 compétences</h2>
            </div>
            <Link to="/cours/eleve/voiture" className="text-white/50 hover:text-white text-[12.5px] font-semibold inline-flex items-center gap-1">
              Ouvrir <ArrowRight size={12} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {COURS_VOITURE_ELEVE.map((c) => (
              <Link
                key={c.code}
                to={`/cours/eleve/voiture?remc=${c.code}a`}
                className="rounded-2xl border border-white/10 bg-[#0d0d0e] hover:border-emerald-500/50 hover:bg-emerald-950/10 p-4 transition"
                data-testid={`remc-comp-${c.code}`}
              >
                <div className="text-emerald-300 font-mono-spec text-[11.5px] font-bold uppercase tracking-wider">{c.code}</div>
                <div className="mt-1.5 text-white font-serif-editorial text-[15px] leading-snug">{c.title || c.titre}</div>
                <div className="mt-2 text-white/45 text-[11.5px] font-mono-spec">{c.lecons?.length || 0} leçons</div>
              </Link>
            ))}
          </div>
        </div>

        {/* Séances récentes */}
        <div className="grid lg:grid-cols-2 gap-4" data-testid="section-recent">
          {/* Fiches de séance */}
          <div>
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="font-serif-editorial text-white text-[20px]">Fiches de séance récentes</h3>
              <Link to="/enseignant/fiches" className="text-white/50 hover:text-white text-[12px] font-semibold inline-flex items-center gap-1">
                Tout voir <ArrowRight size={11} />
              </Link>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-2">
              {loading ? (
                <div className="p-6 text-center text-white/40 text-[12.5px]"><Loader2 size={16} className="animate-spin inline mr-2" /> Chargement…</div>
              ) : sheets.length === 0 ? (
                <div className="p-6 text-center text-white/50 text-[13px]">
                  Aucune fiche pour le moment.
                  <Link to="/enseignant/fiche-seance/nouvelle" className="block mt-2 text-red-300 font-semibold hover:text-red-200">Créer ta 1re fiche →</Link>
                </div>
              ) : (
                <ul className="divide-y divide-white/5">
                  {sheets.slice(0, 5).map((s) => (
                    <li key={s.id}>
                      <Link to={`/fiche/${s.token}`} className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.02] rounded">
                        <FileText size={13} className="text-red-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-[13px] font-semibold truncate">{s.student_name}</div>
                          <div className="text-white/45 text-[11.5px] font-mono-spec">{s.date ? new Date(s.date).toLocaleDateString('fr-FR') : ''}</div>
                        </div>
                        <ArrowRight size={12} className="text-white/30" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          {/* Examens blancs */}
          <div>
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="font-serif-editorial text-white text-[20px]">Examens blancs récents</h3>
              <Link to="/enseignant/examen-blanc-grille" className="text-white/50 hover:text-white text-[12px] font-semibold inline-flex items-center gap-1">
                Nouveau <Plus size={11} />
              </Link>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-2">
              {loading ? (
                <div className="p-6 text-center text-white/40 text-[12.5px]"><Loader2 size={16} className="animate-spin inline mr-2" /> Chargement…</div>
              ) : exams.length === 0 ? (
                <div className="p-6 text-center text-white/50 text-[13px]">
                  Aucun examen blanc pour le moment.
                  <Link to="/enseignant/examen-blanc-grille" className="block mt-2 text-red-300 font-semibold hover:text-red-200">Ouvrir la grille →</Link>
                </div>
              ) : (
                <ul className="divide-y divide-white/5">
                  {exams.slice(0, 5).map((e) => (
                    <li key={e.id}>
                      <Link to={`/examen-blanc/${e.token}`} className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.02] rounded">
                        <ClipboardCheck size={13} className="text-red-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-[13px] font-semibold truncate">{e.student_name}</div>
                          <div className="text-white/45 text-[11.5px] font-mono-spec">
                            {e.date ? new Date(e.date).toLocaleDateString('fr-FR') : ''}
                            {e.ok_pct != null && <> · {e.ok_pct}%</>}
                          </div>
                        </div>
                        <ArrowRight size={12} className="text-white/30" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function RvpCard({ rvp }) {
  const Icon = RVP_ICONS[rvp.icon] || Presentation;
  const c = RVP_COLOR[rvp.color] || RVP_COLOR.red;
  return (
    <div className={`rounded-2xl border ${c.border} ${c.bg} p-5 flex flex-col`} data-testid={`rvp-card-${rvp.slug}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-11 h-11 rounded-xl border ${c.border} ${c.bg} flex items-center justify-center`}>
          <Icon size={20} className={c.accent} />
        </div>
        {rvp.participantsCount > 0 && (
          <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full border border-sky-500/40 bg-sky-950/30 text-sky-200 text-[10.5px] font-bold font-mono-spec">
            <Users size={9} /> {rvp.participantsCount}
          </span>
        )}
      </div>
      <div className={`${c.accent} font-mono-spec text-[11px] font-bold uppercase tracking-wider mb-1`}>
        {rvp.code}{rvp.officiel && ' · Officiel'}
      </div>
      <div className="font-serif-editorial text-white text-[18px] leading-tight mb-1">{rvp.title}</div>
      <div className="text-white/50 text-[12px] leading-snug flex-1">{rvp.subtitle}</div>
      <div className="mt-3 pt-3 border-t border-white/5 text-white/45 text-[11px] font-mono-spec">
        {rvp.duration}
      </div>
      <div className="mt-3 flex gap-2">
        <Link
          to={`/enseignant/rendez-vous-pedagogiques/${rvp.slug}`}
          data-testid={`rvp-launch-${rvp.slug}`}
          className={`flex-1 h-10 rounded-full ${c.button} text-[12.5px] font-bold inline-flex items-center justify-center gap-1.5`}
        >
          <Play size={12} /> Lancer
        </Link>
        <Link
          to={`/enseignant/rendez-vous-pedagogiques/${rvp.slug}?mode=pro`}
          data-testid={`rvp-launch-pro-${rvp.slug}`}
          title="Mode PRO — vidéoprojection"
          className="h-10 px-3 rounded-full border border-white/15 text-white/80 hover:border-white/35 text-[12px] font-semibold inline-flex items-center justify-center"
        >
          <Presentation size={12} />
        </Link>
      </div>
    </div>
  );
}
